import {
  BoundNumberField,
  Css,
  GridColumn,
  GridDataRow,
  GridTable,
  IconButton,
  RowStyles,
  Tooltip,
  collapseColumn,
  column,
  emptyCell,
  numericColumn,
} from "@homebound/beam";
import { FieldState, ObjectState } from "@homebound/form-state";
import { useMemo } from "react";
import { InputMaybe } from "src/generated/graphqlTypes";
import {
  PlanBasedBidsTable_ReadyPlanFragment,
  PlanBasedBidsTable_ReadyPlanOptionFragment,
  PlanBasedBidsTable_TakeoffLineItemVersionFragment,
} from "src/routes/bids/components/PlanBasedBidsTable.generated";
import { EditBidContractTradeLineItemFragment } from "src/routes/bids/components/ProrateBidModal.generated";
import { DropCodeCell } from "src/routes/developments/product-offerings/components/DropCodeCell";
import { fail, groupBy, roundToDigits } from "src/utils";
import { getOptionsKey } from "../BidsTab";
import { getQuantity } from "../PlanBasedBidsTable";
import { FormValues, ProrationFormValues } from "../ProrateBidModal";
import { ProrationCostColumn, ProrationUnitCostColumn } from "./ProrationCostColumn";

export function initializePlanAndOptionFormState(
  readyPlan: PlanBasedBidsTable_ReadyPlanFragment,
  scopeLineItems: PlanBasedBidsTable_TakeoffLineItemVersionFragment[],
  existingTradeLineItems: EditBidContractTradeLineItemFragment[],
): FormValues {
  const groupedTLIs = groupBy(scopeLineItems, (tli) => readyPlan.id + getOptionsKey(tli.options));

  return {
    bidContractTradeLineItems: Object.entries(groupedTLIs).map(([key, tlis]) => {
      // The existing trade line item would match the product offering and options combination
      const existingTradeLineItem = existingTradeLineItems.find(
        (tli) => tli.productOffering.id + getOptionsKey(tli.options) === key,
      );

      return {
        id: existingTradeLineItem?.id,
        unitCostInCents: undefined,
        totalCostInCents: existingTradeLineItem?.totalCostInCents,

        productOfferingId: readyPlan.id,
        optionIds: tlis[0].options.sortByKey("id").map((o) => o.id),
        prorations: tlis.map((tli) => {
          // The existing line item would match the item template item as it is within the same product offering and options
          const existingProration = existingTradeLineItem?.lineItems.find((li) => li.itemTemplateItem?.id === tli.id);
          return {
            id: existingProration?.id,
            quantity: getQuantity(tli),
            unitCostInCents: existingProration
              ? roundToDigits(existingProration.totalCostInCents / getQuantity(tli), 1)
              : undefined,
            costInCents: existingProration?.totalCostInCents,
            estimatedCostInCents: tli.costEstimate.costInCents,
            userEntered: false,

            itemTemplateItemId: tli.id,
          };
        }),
      };
    }),
  };
}

type HeaderRow = { kind: "head"; data: undefined };
type OptionRow = {
  kind: "option";
  data: { options: PlanBasedBidsTable_ReadyPlanOptionFragment[]; totalCostField: FieldState<InputMaybe<number>> };
};
type TLIRow = {
  kind: "tli";
  data: {
    itemTemplateItem: PlanBasedBidsTable_TakeoffLineItemVersionFragment;
    prorationState: ObjectState<ProrationFormValues>;
  };
};

type Row = HeaderRow | ProductOfferingRow | OptionRow | TLIRow;

export type ReadyPlanOptionsById = Record<string, PlanBasedBidsTable_ReadyPlanOptionFragment>;

type PlanAndOptionTablesProps = {
  formState: ObjectState<FormValues>;
  readyPlan: PlanBasedBidsTable_ReadyPlanFragment;
  scopeLineItems: PlanBasedBidsTable_TakeoffLineItemVersionFragment[];
  /** A map of option.id -> data, b/c it's too repetitive to embed in TakeoffLineItemVersionFragment. */
  readyPlanOptionsById: ReadyPlanOptionsById;
  tradePartnerName?: string;
};

export function PlanAndOptionTables(props: PlanAndOptionTablesProps) {
  const { formState, readyPlan, scopeLineItems, readyPlanOptionsById, tradePartnerName } = props;
  /*
    Instead of showing all options with a location, we want to show options that share a global option in different locations or are grouped by feature.
    Right now we are only ever rendering one product offering, so we don't need to group by product offering.
  */
  const columns = useMemo(() => {
    const allOptions = scopeLineItems
      .flatMap((scopeLine) => getReadyPlanOptions(readyPlanOptionsById, scopeLine.options))
      .unique();
    return createColumns(getOptionsInMultipleLocations(allOptions).map((o) => o.id));
  }, [readyPlanOptionsById, scopeLineItems]);

  const rows = useMemo(() => {
    return createRows(readyPlan, formState, scopeLineItems, readyPlanOptionsById, tradePartnerName);
  }, [readyPlan, formState, scopeLineItems, readyPlanOptionsById, tradePartnerName]);

  return (
    <div css={Css.df.fdc.gap3.w100.$}>
      <GridTable columns={columns} rows={rows} rowStyles={rowStyles} />
    </div>
  );
}

const rowStyles: RowStyles<Row> = { productOffering: { cellCss: Css.py3.$ }, head: { cellCss: Css.sm.$ } };

function createRows(
  productOffering: PlanBasedBidsTable_ReadyPlanFragment,
  formState: ObjectState<FormValues>,
  scopeLineItems: PlanBasedBidsTable_TakeoffLineItemVersionFragment[],
  readyPlanOptionsById: ReadyPlanOptionsById,
  tradePartnerName?: string,
): GridDataRow<Row>[] {
  const itemTemplateItems = scopeLineItems;

  const optionGroupBy = groupBy(itemTemplateItems, (tli) => getOptionsKey(tli.options));
  const optionRows = Object.keys(optionGroupBy).map((key) => {
    const children = optionGroupBy[key];
    const tli = children[0];
    const bidContractTradeLineItem = formState.bidContractTradeLineItems.rows.find(
      (bcli) => bcli.productOfferingId.value === productOffering.id && (bcli.optionIds.value || []).join(",") === key,
    );
    if (!bidContractTradeLineItem) {
      fail("BidContractTradeLineItem not found for product offering and options");
    }
    return {
      kind: "option" as const,
      id: key || "base",
      data: {
        options: getReadyPlanOptions(readyPlanOptionsById, tli.options),
        totalCostField: bidContractTradeLineItem.totalCostInCents,
      },
      children: children.map((itemTemplateItem) => {
        const prorationState = bidContractTradeLineItem.prorations.rows.find(
          (p) => p.itemTemplateItemId.value === itemTemplateItem.id,
        );
        if (!prorationState) {
          fail("prorationLineItem not found for item template item");
        }
        return { kind: "tli" as const, id: itemTemplateItem.id, data: { itemTemplateItem, prorationState } };
      }),
    };
  });

  return [
    {
      kind: "productOffering" as const,
      id: "productOffering",
      data: { productOfferingName: productOffering.name, tradePartnerName },
      fixedSort: "first",
    },
    { kind: "head" as const, id: "head", data: undefined, fixedSort: "last" },
    ...optionRows,
  ];
}

type ProductOfferingRow = { kind: "productOffering"; data: { productOfferingName: string; tradePartnerName?: string } };

function createColumns(optionsWithMultipleGlobalOptions: string[]): GridColumn<Row>[] {
  return [
    collapseColumn<Row>({
      productOffering: ({ productOfferingName, tradePartnerName }) => ({
        content: (
          <div css={Css.df.w100.aic.$}>
            <div css={Css.xl.$}>
              {productOfferingName} {tradePartnerName && <span css={Css.gray600.$}>{`— ${tradePartnerName} Bid`}</span>}
            </div>
          </div>
        ),
        colspan: 10,
      }),
      // This functions as a header/parent toggle to collapse/expand all option rows. It needs to be hand written because we don't have a true header row kind.
      head: (data, { api }) => {
        const collapsed = api.getVisibleRows("option").every((r) => api.isCollapsedRow(r.id));
        return (
          <IconButton
            onClick={() =>
              api.getVisibleRows("option").forEach((r) => {
                if (collapsed && api.isCollapsedRow(r.id)) {
                  api.toggleCollapsedRow(r.id);
                } else if (!collapsed && !api.isCollapsedRow(r.id)) {
                  api.toggleCollapsedRow(r.id);
                }
              })
            }
            icon={collapsed ? "chevronRight" : "chevronDown"}
          />
        );
      },
    }),
    column<Row>({
      productOffering: emptyCell,
      head: "Code",
      option: emptyCell,
      tli: ({ itemTemplateItem }) => <DropCodeCell scope={itemTemplateItem} />,
      w: "100px",
    }),
    column<Row>({
      productOffering: emptyCell,
      head: "Name",
      option: ({ options }) => getOptionNameCell(options, optionsWithMultipleGlobalOptions),
      tli: ({ itemTemplateItem }) => itemTemplateItem.name,
    }),
    column<Row>({
      productOffering: emptyCell,
      head: "Material Code",
      option: emptyCell,
      tli: ({ itemTemplateItem }) => itemTemplateItem.bidItem?.parentMaterialVariant?.code,
      // w: "120px", are the material codes going to be this long?
    }),
    column<Row>({
      productOffering: emptyCell,
      head: "Qty",
      option: emptyCell,
      tli: ({ itemTemplateItem }) => getQuantity(itemTemplateItem),
      w: "53px",
    }),
    column<Row>({
      productOffering: emptyCell,
      head: "UoM",
      option: emptyCell,
      tli: ({ itemTemplateItem }) => itemTemplateItem.unitOfMeasure.shortName,
      w: "67px",
    }),
    column<Row>({
      productOffering: emptyCell,
      head: "Location",
      option: emptyCell,
      tli: ({ itemTemplateItem }) => itemTemplateItem.location.displayLocationPath,
      w: "160px",
    }),
    numericColumn<Row>({
      productOffering: emptyCell,
      head: "Cost",
      option: emptyCell,
      tli: ProrationUnitCostColumn,
      w: "160px",
    }),
    numericColumn<Row>({
      productOffering: emptyCell,
      head: "Total Cost",
      option: ({ totalCostField }) => <BoundNumberField field={totalCostField} />,
      tli: ProrationCostColumn,
      w: "160px",
    }),
  ];
}

export function getOptionsInMultipleLocations(
  options: PlanBasedBidsTable_ReadyPlanOptionFragment[],
): PlanBasedBidsTable_ReadyPlanOptionFragment[] {
  const optionWithDifferingGlobalOptionLocations = options
    .groupBy((o) => o.globalOption.id)
    .toValues()
    .filter((options) => options.length > 1)
    .flatMap((o) => o);
  const groupByFeatureOptions = options.filter((o) => o.optionGroup.groupByFeature);
  return [...optionWithDifferingGlobalOptionLocations, ...groupByFeatureOptions];
}

/** Resolves option refs with ids into table-wide option details. I.e. [{ id: "rpo:1" }] -> [option]. */
export function getReadyPlanOptions(
  readyPlanOptionsById: ReadyPlanOptionsById,
  options: { id: string }[],
): PlanBasedBidsTable_ReadyPlanOptionFragment[] {
  return options.map((option) => readyPlanOptionsById[option.id] ?? fail(`ReadyPlanOption not found: ${option.id}`));
}

/**
 * Renders the "Name" cell for an option row: a comma-separated list of option names (each with a
 * tooltip showing its option type), or "Base" when there are no options. The `value` is the plain-text
 * version used for sorting/filtering.
 */
export function getOptionNameCell(
  options: PlanBasedBidsTable_ReadyPlanOptionFragment[],
  optionsWithMultipleGlobalOptions: string[],
) {
  const getLabel = (o: PlanBasedBidsTable_ReadyPlanOptionFragment) =>
    optionsWithMultipleGlobalOptions.includes(o.id) ? `${o.name} - ${o.location?.name}` : o.name;
  return {
    content:
      options.length === 0 ? (
        "Base"
      ) : (
        <span>
          {options.map((o, i) => (
            <span key={o.id}>
              {i > 0 && ", "}
              <Tooltip title={o.optionGroup.name ?? o.optionGroup.name} placement="top">
                <span>{getLabel(o)}</span>
              </Tooltip>
            </span>
          ))}
        </span>
      ),
    value: options.map(getLabel).join(", ") || "Base",
    css: Css.smSb.$,
  };
}
